/**
 * Environment Detection Utility
 * Detects embedded browsers, unsupported browsers, and platform information
 */

export interface EnvironmentInfo {
  isEmbedded: boolean;
  isUnsupported: boolean;
  browserName: string;
  platform: string;
  userAgent: string;
  detectedIssues: string[];
}

/**
 * List of embedded browser user agent patterns
 */
const EMBEDDED_BROWSER_PATTERNS = [
  /FBAN|FBAV/i, // Facebook in-app browser
  /Instagram/i, // Instagram in-app browser
  /Twitter/i, // Twitter in-app browser
  /LinkedIn/i, // LinkedIn in-app browser
  /Snapchat/i, // Snapchat in-app browser
  /TikTok/i, // TikTok in-app browser
  /Line\//i, // LINE in-app browser
  /MicroMessenger/i, // WeChat in-app browser
  /\bwv\)/i, // Generic WebView indicator (Android)
];

/**
 * List of unsupported browser patterns
 * These browsers have known issues with WebRTC or our player
 */
const UNSUPPORTED_BROWSER_PATTERNS = [
  /MSIE|Trident/i, // Internet Explorer
  // Add more as needed
];

/**
 * Detect if running in an embedded/in-app browser
 */
export function isEmbeddedBrowser(userAgent: string = navigator.userAgent): boolean {
  // Check standard embedded browser patterns
  if (EMBEDDED_BROWSER_PATTERNS.some((pattern) => pattern.test(userAgent))) {
    return true;
  }

  // iOS WKWebView detection: iOS device without "Safari/" in user agent
  // Real Safari includes "Safari/604.1" or similar, WKWebView does not
  // Note: Most iOS in-app browsers (Discord, Slack, etc.) include Safari/ identifier
  // This primarily catches bare WKWebView implementations and some preview windows
  const isIOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
  const hasSafariIdentifier = /Safari\//i.test(userAgent);

  if (isIOSDevice && !hasSafariIdentifier) {
    return true;
  }

  return false;
}

/**
 * Detect if running in an unsupported browser
 */
export function isUnsupportedBrowser(userAgent: string = navigator.userAgent): boolean {
  return UNSUPPORTED_BROWSER_PATTERNS.some((pattern) => pattern.test(userAgent));
}

/**
 * Detect browser name from user agent
 */
export function detectBrowserName(userAgent: string = navigator.userAgent): string {
  if (/Firefox/i.test(userAgent)) return 'Firefox';
  if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) return 'Chrome';
  if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) return 'Safari';
  if (/Edg/i.test(userAgent)) return 'Edge';
  if (/Opera|OPR/i.test(userAgent)) return 'Opera';
  if (/MSIE|Trident/i.test(userAgent)) return 'Internet Explorer';

  // Check for embedded browsers
  if (/FBAN|FBAV/i.test(userAgent)) return 'Facebook';
  if (/Instagram/i.test(userAgent)) return 'Instagram';
  if (/Twitter/i.test(userAgent)) return 'Twitter';
  if (/LinkedIn/i.test(userAgent)) return 'LinkedIn';
  if (/Snapchat/i.test(userAgent)) return 'Snapchat';
  if (/TikTok/i.test(userAgent)) return 'TikTok';
  if (/Line\//i.test(userAgent)) return 'LINE';
  if (/MicroMessenger/i.test(userAgent)) return 'WeChat';

  // iOS WKWebView (Safari preview, in-app browser)
  if (/iPhone|iPad|iPod/i.test(userAgent) && !/Safari/i.test(userAgent)) {
    return 'Safari Preview';
  }

  return 'Unknown';
}

/**
 * Detect platform (iOS, Android, Desktop, etc.)
 */
export function detectPlatform(userAgent: string = navigator.userAgent): string {
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';

  return 'Unknown';
}

/**
 * Get default browser for the platform
 * Used to suggest which browser to open the link in
 */
export function getDefaultBrowserForPlatform(platform: string): string {
  switch (platform) {
    case 'iOS':
      return 'Safari';
    case 'Android':
      return 'Chrome';
    case 'Windows':
    case 'macOS':
    case 'Linux':
      return 'your default browser';
    default:
      return 'your browser';
  }
}

/**
 * Comprehensive environment detection
 */
export function detectEnvironment(): EnvironmentInfo {
  const userAgent = navigator.userAgent;
  const isEmbedded = isEmbeddedBrowser(userAgent);
  const isUnsupported = isUnsupportedBrowser(userAgent);
  const browserName = detectBrowserName(userAgent);
  const platform = detectPlatform(userAgent);

  const detectedIssues: string[] = [];

  if (isEmbedded) {
    detectedIssues.push(`Detected embedded browser: ${browserName}`);
  }

  if (isUnsupported) {
    detectedIssues.push(`Unsupported browser: ${browserName}`);
  }

  return {
    isEmbedded,
    isUnsupported,
    browserName,
    platform,
    userAgent,
    detectedIssues,
  };
}

/**
 * Check if environment blocks the quickstart flow
 * TEMPORARILY DISABLED - Always allows quickstart to proceed
 */
export function shouldBlockQuickstart(env: EnvironmentInfo): boolean {
  // return env.isEmbedded || env.isUnsupported;
  return false; // Temporarily disabled - allow all browsers
}

/**
 * Get the current page URL (for redirecting to default browser)
 */
export function getCurrentPageUrl(): string {
  return window.location.href;
}

